import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { t, pickMulti } from "../i18n/translations";
import { api } from "../lib/api";
import { ArrowUpRight, Clock, Tag, ChevronDown, Check, MessageCircle, Calendar, HelpCircle } from "lucide-react";

/**
 * AVAILABILITY_STATUS – Verfügbarkeits-Indikator.
 *
 * ⚠️  MANUELL AKTUALISIEREN, wenn Kalender voll ist!
 * Sonst = Vertrauens-Bruch, wenn Kundinnen anrufen und keine Termine da sind.
 *
 * Erlaubte Werte:
 *   "this_week"   → grün, "Diese Woche"
 *   "next_week"   → grün, "Nächste Woche"
 *   "2_3_weeks"   → gelb, "In 2-3 Wochen"
 */
const AVAILABILITY_STATUS = "this_week";

export default function Services() {
  const { lang } = useLang();
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
  }, []);

  const toggle = (id, k) => setOpen((s) => ({ ...s, [`${id}_${k}`]: !s[`${id}_${k}`] }));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20" data-testid="services-page">
      <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">— {t(lang, "services.title")}</div>
      <h1 className="font-display text-5xl lg:text-6xl text-ink leading-tight max-w-3xl">{t(lang, "services.subtitle")}</h1>

      {/* Compact list overview */}
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="services-overview-grid">
        {services.map((svc) => (
          <a key={svc.id} href={`#${svc.slug}`} className="group bg-white border border-border rounded-md overflow-hidden hover:border-terracotta/50 transition-colors" data-testid={`svc-tile-${svc.slug}`}>
            <div className="aspect-square overflow-hidden bg-muted">
              <img src={svc.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-3">
              <div className="font-display text-base text-ink leading-tight">{pickMulti(svc.name, lang)}</div>
              <div className="text-[11px] text-ink-muted mt-1 font-mono">{svc.duration_minutes} min · CHF {svc.price_chf}</div>
            </div>
          </a>
        ))}
      </div>

      {/* Detailed sections */}
      <div className="mt-20 space-y-24">
        {services.map((svc, idx) => {
          const ind = pickMulti(svc.indications, lang);
          const eff = pickMulti(svc.effects, lang);
          const con = pickMulti(svc.contraindications, lang);
          const inc = pickMulti(svc.includes, lang);
          return (
            <article id={svc.slug} key={svc.id} className={`grid lg:grid-cols-12 gap-8 lg:gap-14 items-start scroll-mt-24 ${idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`service-detail-${svc.slug}`}>
              <div className="lg:col-span-6">
                <div className="aspect-[4/3] overflow-hidden rounded-md bg-muted">
                  <img src={svc.image_url} alt={pickMulti(svc.name, lang)} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </div>
              <div className="lg:col-span-6">
                <h2 className="font-display text-4xl text-ink leading-tight">{pickMulti(svc.name, lang)}</h2>
                <div className="mt-4 flex flex-wrap gap-5 text-sm">
                  <span className="inline-flex items-center gap-2 text-ink"><Clock className="w-4 h-4 text-sage" strokeWidth={1.5} /> {svc.duration_minutes} {t(lang, "services.minutes")}</span>
                  <span className="inline-flex items-center gap-2 text-ink"><Tag className="w-4 h-4 text-sage" strokeWidth={1.5} /> CHF {svc.price_chf}</span>
                </div>
                <p className="mt-5 text-ink-muted leading-relaxed whitespace-pre-line">{pickMulti(svc.description, lang)}</p>

                {inc && (
                  <div className="mt-5 text-sm italic text-ink-muted border-l-2 border-terracotta/40 pl-4 py-1">{inc}</div>
                )}

                <div className="mt-6 space-y-1 border-t border-border pt-4">
                  {ind && <Accordion title={t(lang, "services.indications")} open={open[`${svc.id}_ind`]} onClick={() => toggle(svc.id, "ind")} content={ind} testid={`acc-ind-${svc.slug}`} color="sage" />}
                  {eff && <Accordion title={t(lang, "services.effects")} open={open[`${svc.id}_eff`]} onClick={() => toggle(svc.id, "eff")} content={eff} testid={`acc-eff-${svc.slug}`} color="terracotta" />}
                  {con && <Accordion title={t(lang, "services.contraindications")} open={open[`${svc.id}_con`]} onClick={() => toggle(svc.id, "con")} content={con} testid={`acc-con-${svc.slug}`} color="ink" />}
                </div>

                <Link to={`/booking?service=${svc.id}`} className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors" data-testid={`service-book-${svc.slug}`}>
                  {t(lang, "services.book")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </article>
          );
        })}
      </div>

      {/* CONVERSION FOOTER — Preistransparenz + Multi-Session-Hint + Verfügbarkeit + Dual-CTA */}
      <ConversionFooter lang={lang} />
    </div>
  );
}

const Accordion = ({ title, content, open, onClick, testid, color }) => {
  const dotColor = color === "sage" ? "bg-sage" : color === "terracotta" ? "bg-terracotta" : "bg-ink";
  return (
    <div className="border-b border-border" data-testid={testid}>
      <button onClick={onClick} className="w-full flex items-center justify-between py-3.5 text-left group">
        <span className="text-sm uppercase tracking-wider text-ink flex items-center gap-2.5">
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
          {title}
        </span>
        <ChevronDown className={`w-4 h-4 text-ink-muted transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="pb-4 text-sm text-ink-muted leading-relaxed">
          {content.split(" • ").map((item, i) => (
            <div key={i} className="flex gap-2 py-0.5"><span className="text-terracotta">·</span><span>{item}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------------------
 * Conversion Footer
 *
 * Additive block at the bottom of /massagen that increases conversion by:
 *   1. Making the always-included value tangible (global inclusion list)
 *   2. Testing package demand honestly (WhatsApp deep link, no fake feature)
 *   3. Reducing decision anxiety (transparent availability window)
 *   4. Offering two commit levels (book now vs. ask a question)
 *
 * No changes to the booking flow, DB or existing detail sections.
 * ---------------------------------------------------------------------------
 */

// WhatsApp business number (E.164 without + or spaces). Change here if it moves.
const WHATSAPP_NUMBER = "41782650999";

const FOOTER_COPY = {
  de: {
    eyebrow: "— Deine Vorteile auf einen Blick",
    heading: "Klar, ehrlich, ohne Überraschungen.",
    subheading:
      "Alle Preise inklusive, keine versteckten Zusatzkosten. Bezahlt wird bequem vor Ort in bar, per Twint oder Kreditkarte.",
    includedTitle: "Bei jeder Behandlung inklusive",
    included: [
      "Persönliches Vorgespräch zur Anamnese",
      "Warmes, biologisches Ayurveda-Öl auf deine Konstitution abgestimmt",
      "Klangschalen-Ritual zu Beginn und zum Abschluss",
      "Ruhige Nachliegezeit auf einer beheizten Liege",
      "Frisch aufgebrühter Ayurveda-Tee zum Abschluss",
    ],
    packageTitle: "Mehrfach-Buchung?",
    packageBody:
      "Schreib uns einfach das Stichwort «PAKET» auf WhatsApp – wir gewähren 10–15 % Rabatt ab 3 Terminen.",
    packageCta: "«PAKET» auf WhatsApp senden",
    packageMessage:
      "Hallo, ich interessiere mich für ein Paket. Bitte sende mir Details zu den Paket-Rabatten.",
    availability: {
      this_week: { tone: "good", label: "Nächster Termin verfügbar: Diese Woche" },
      next_week: { tone: "good", label: "Nächster Termin verfügbar: Nächste Woche" },
      "2_3_weeks": { tone: "warn", label: "Aktuell begrenzte Verfügbarkeit: In 2-3 Wochen" },
    },
    ctaBook: "Jetzt Termin buchen",
    ctaAsk: "Frage stellen",
    ctaAskSubject:
      "Frage zu einer Behandlung",
    ctaFootnote:
      "Antwort in der Regel innerhalb von 24 Stunden (Mo–Sa).",
  },
  en: {
    eyebrow: "— Your Benefits At A Glance",
    heading: "Clear, honest, no surprises.",
    subheading:
      "All prices are all-inclusive – no hidden extras. Payment on site by cash, Twint or credit card.",
    includedTitle: "Included in every treatment",
    included: [
      "Personal consultation to understand your needs",
      "Warm, organic Ayurveda oil matched to your constitution",
      "Singing bowl ritual at the start and end",
      "Quiet rest on a heated treatment bed",
      "Freshly brewed Ayurveda tea to close",
    ],
    packageTitle: "Booking multiple sessions?",
    packageBody:
      "Just send us the keyword «PAKET» on WhatsApp – we grant 10-15 % off starting from 3 sessions.",
    packageCta: "Send «PAKET» on WhatsApp",
    packageMessage:
      "Hi, I'm interested in a package. Please send me details about the multi-session discount.",
    availability: {
      this_week: { tone: "good", label: "Next appointment available: This week" },
      next_week: { tone: "good", label: "Next appointment available: Next week" },
      "2_3_weeks": { tone: "warn", label: "Currently limited availability: In 2-3 weeks" },
    },
    ctaBook: "Book your appointment",
    ctaAsk: "Ask a question",
    ctaAskSubject: "Question about a treatment",
    ctaFootnote: "Usually answered within 24 hours (Mon–Sat).",
  },
};

// Fires a GA4 event when the conversion section becomes visible.
function usePricingViewTracking(ref) {
  useEffect(() => {
    if (!ref.current || typeof window === "undefined") return;
    const el = ref.current;
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !fired) {
            fired = true;
            try {
              if (typeof window.gtag === "function") {
                window.gtag("event", "view_pricing_details", { source: "services_page" });
              }
            } catch (_) {}
          }
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
}

function trackFooter(name, params) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch (_) {}
}

function ConversionFooter({ lang }) {
  const baseCopy = FOOTER_COPY[lang] || FOOTER_COPY.de;
  const [pkg, setPkg] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${backend}/api/settings`);
        if (!r.ok) return;
        const s = await r.json();
        if (!alive) return;
        setPkg({
          title: s[`package_title_${lang}`] || s.package_title_de,
          body: s[`package_body_${lang}`] || s.package_body_de,
          cta: s[`package_cta_${lang}`] || s.package_cta_de,
        });
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, [lang]);

  const copy = {
    ...baseCopy,
    packageTitle: (pkg && pkg.title) || baseCopy.packageTitle,
    packageBody: (pkg && pkg.body) || baseCopy.packageBody,
    packageCta: (pkg && pkg.cta) || baseCopy.packageCta,
  };
  const avail = copy.availability[AVAILABILITY_STATUS] || copy.availability.this_week;
  const availClass =
    avail.tone === "warn"
      ? "bg-amber-50 border-amber-500 text-amber-900"
      : "bg-emerald-50 border-emerald-500 text-emerald-900";
  const availDotClass = avail.tone === "warn" ? "bg-amber-500" : "bg-emerald-500";

  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(copy.packageMessage)}`;
  const mailHref = `mailto:contact@ayurveda-aargau.ch?subject=${encodeURIComponent(copy.ctaAskSubject)}`;

  const sectionRef = React.useRef(null);
  usePricingViewTracking(sectionRef);

  return (
    <section
      ref={sectionRef}
      className="mt-24 pt-16 border-t border-border"
      aria-labelledby="services-conversion-heading"
      data-testid="services-conversion-footer"
    >
      <div className="max-w-4xl">
        <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{copy.eyebrow}</div>
        <h2
          id="services-conversion-heading"
          className="font-display text-4xl lg:text-5xl text-ink leading-tight"
        >
          {copy.heading}
        </h2>
        <p className="mt-5 text-ink-muted leading-relaxed">{copy.subheading}</p>
      </div>

      {/* Inclusive list */}
      <div className="mt-12 grid md:grid-cols-2 gap-10 items-start">
        <div
          className="bg-sand-soft border border-border rounded-md p-8"
          data-testid="services-included-block"
        >
          <div className="text-sm uppercase tracking-wider text-ink mb-5 font-medium">
            {copy.includedTitle}
          </div>
          <ul className="space-y-3">
            {copy.included.map((item, i) => (
              <li key={i} className="flex gap-3 text-ink leading-relaxed">
                <span className="w-5 h-5 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3" strokeWidth={2.5} />
                </span>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Multi-session WhatsApp CTA */}
        <div
          className="border border-terracotta/30 bg-white rounded-md p-8"
          data-testid="services-package-cta"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-2xl text-ink leading-snug">
                {copy.packageTitle}
              </div>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">{copy.packageBody}</p>
            </div>
          </div>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackFooter("contact", {
                source: "package_inquiry",
                method: "whatsapp",
              })
            }
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full hover:bg-[#1ea952] transition-colors text-sm font-medium"
            data-testid="services-package-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2} />
            {copy.packageCta}
          </a>
        </div>
      </div>

      {/* Availability + Dual-CTA */}
      <div className="mt-12">
        <div
          className={`inline-flex items-center gap-2.5 px-4 py-2 border-l-4 rounded ${availClass}`}
          data-testid="services-availability"
        >
          <span className={`w-2 h-2 rounded-full ${availDotClass} animate-pulse`} aria-hidden="true" />
          <span className="text-sm font-medium">{avail.label}</span>
        </div>

        <div className="mt-6 flex flex-wrap gap-4 items-center">
          <Link
            to="/booking"
            onClick={() =>
              trackFooter("generate_lead", { source: "services_pricing", item_id: "general" })
            }
            className="inline-flex items-center gap-2 px-8 py-4 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors font-medium"
            data-testid="services-footer-book-cta"
          >
            <Calendar className="w-4 h-4" strokeWidth={1.5} />
            {copy.ctaBook}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <a
            href={mailHref}
            onClick={() => trackFooter("contact", { source: "services_pricing", method: "email" })}
            className="inline-flex items-center gap-2 px-8 py-4 border border-ink text-ink rounded-full hover:bg-ink hover:text-white transition-colors font-medium"
            data-testid="services-footer-ask-cta"
          >
            <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
            {copy.ctaAsk}
          </a>
        </div>

        <p className="mt-4 text-xs text-ink-muted">{copy.ctaFootnote}</p>
      </div>
    </section>
  );
}
